import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from "@angular/router";
import { Observable, Subscription, forkJoin } from "rxjs";
import { tap, map } from "rxjs/operators";

import { LoaderService } from "../../../common/services/loader.service";

import { SortOption, SortArgs, SortDirection } from "../../../core/models/sort.model";
import { HelpNavigationItem, HelpCategory, HelpNav } from "../../models/help-library.model";
import { HelpLibraryService } from "../../services/help-library.service";

@Component({
    selector: 'app-help-library-navigation',
    templateUrl: './help-library-navigation.component.html',
    styleUrls: ['./help-library-navigation.component.scss'],
    standalone: false
})
export class HelpLibraryNavigationComponent implements OnInit, OnDestroy {
    static componentName = "HelpLibraryNavigationComponent";

    categories: HelpCategory[] = [];

    currentSearchFilterTerm: string = "";
    currentSort: SortArgs = null;

    fullList: HelpNav[];
    loadingActive: boolean = true;
    navItems: HelpNav[] = [];


    sortOptions: SortOption[] = [{ value: "title", display: "Title" } as SortOption, { value: "category", display: "Category" } as SortOption];
    subscriptions: Subscription = new Subscription();

    title: string = "Available Topics";


    constructor(
        public router: Router,
        public loader: LoaderService,
        private readonly _libraryService: HelpLibraryService) { }

    ngOnInit() {
        let navObs = this._libraryService.getNavigation(false).pipe();
        let categoryObs = this._libraryService.getCategories().pipe();
        let navItems$ = forkJoin(navObs, categoryObs).pipe(
            map(([navList, categoryList]) => {
                this.categories = categoryList;
                this.fullList = [];
                navList.forEach(n => {
                    let category = this.categories.find(c => c.id === n.categoryId);
                    let navItem = new HelpNav(n, category);
                    this.fullList.push(navItem);
                    this.navItems.push(navItem);
                });
                this._libraryService.setActiveNavItem(null);
                this.loadingActive = false;
            })
        ).subscribe(
            () => { console.log("help navigation loaded"); },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(navItems$);
        let delSubs$ = this._libraryService.onHelpDeleted.subscribe(
            (id) => {
                this.fullList.splice(this.fullList.findIndex(x => x.id === id, 1));
                let index = this.navItems.findIndex(x => x.id === id);
                if (index !== -1) {
                    this.navItems.splice(index, 1);
                }
            },
            (error) => { console.error(error); });
        this.subscriptions.add(delSubs$);

        let updSubs$ = this._libraryService.onHelpUpdated.subscribe(
            (item) => {
                let index = this.fullList.findIndex(x => x.id === item.id);
                let category = this.categories.find(c => c.id === item.categoryId);
                if (index !== -1) {                    
                    this.fullList[index] = new HelpNav(item, category);
                }
                index = this.navItems.findIndex(x => x.id === item.id);
                if (index !== -1) {
                    this.navItems[index] = new HelpNav(item, category);
                }
            },
            (error) => { console.error(error); });
        this.subscriptions.add(updSubs$);

        let createSubs$ = this._libraryService.onHelpCreated.subscribe(
            (item) => {
                console.log("create subscription triggered");
                let category = this.categories.find(c => c.id === item.categoryId);
                this.fullList.unshift(new HelpNav(item, category));
                if (this.currentSort !== null) {
                    this.onSortChanged(this.currentSort);
                }
                if (this.currentSearchFilterTerm.length > 0) {
                    this.onSearchFilter(this.currentSearchFilterTerm);
                }
                if (this.currentSort == null && this.currentSearchFilterTerm.length === 0) {
                    this.navItems = this.fullList;
                }
            },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(createSubs$);

        let categoryUpdate$ = this._libraryService.onCategoryUpdated.subscribe(
            (newCat) => {
                this.fullList.forEach(nav => {
                    if (nav.categoryId === newCat.id) {
                        nav.category = newCat.name;
                    }
                });
                if (this.currentSort !== null) {
                    this.onSortChanged(this.currentSort);
                }
                if (this.currentSearchFilterTerm.length > 0) {
                    this.onSearchFilter(this.currentSearchFilterTerm);
                }
                if (this.currentSort == null && this.currentSearchFilterTerm.length === 0) {
                    this.navItems = this.fullList;
                }
                let index = this.categories.findIndex(c => c.id === newCat.id);
                if (index !== -1) {
                    this.categories[index] = newCat;
                }
            },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(categoryUpdate$);
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    navigate(id: string, categoryId: string): void {
        this.router.navigateByUrl(`help-library/${categoryId}/${id}`);
    }


    onSearchFilter(term: string): void {
        this.navItems = this.fullList.filter(nav => nav.category.indexOf(term) !== -1 || nav.name.indexOf(term) !== -1);
    }

    onSortChanged(args: SortArgs): void {
        this.currentSort = args;
        if (args !== null) {
            switch (args.sortProperty.toUpperCase()) {
                case "CATEGORY":
                    if (args.sortDirection === SortDirection.Ascending) {
                        this.navItems = this.fullList.sort((a, b) => {
                            if (a.category < b.category) return -1;
                            if (a.category > b.category) return 1;
                            return 0;
                        });
                    } else {
                        this.navItems = this.fullList.sort((a, b) => {
                            if (a.category < b.category) return 1;
                            if (a.category > b.category) return -1;
                            return 0;
                        });
                    }
                    break;
                case "TITLE":
                    if (args.sortDirection === SortDirection.Ascending) {
                        this.navItems = this.fullList.sort((a, b) => {
                            if (a.name < b.name) return -1;
                            if (a.name > b.name) return 1;
                            return 0;
                        });
                    } else {
                        this.navItems = this.fullList.sort((a, b) => {
                            if (a.name < b.name) return 1;
                            if (a.name > b.name) return -1;
                            return 0;
                        });
                    }
                    break;
            }
        } else {
            this.navItems = this.fullList;
        }
    }
}
